import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { MenuItem } from 'primereact/menuitem';
import { ReactElement, useEffect, useState } from 'react';
import { TieredMenu } from 'primereact/tieredmenu';
import { Menubar } from 'primereact/menubar';
import { useAuth0 } from '@auth0/auth0-react';
import { useCartContext } from 'CartContext';
import { useMarketplaceDomain } from 'hooks/useMarketplace';
import { useLogin, useLogout } from 'hooks/userHooks';
import useLocalStorage from 'utils/useLocalStorage';
import Logo from 'assets/crwd/brand_market/crwdmarket_icon_b.svg';
import LogoLight from 'assets/crwd/brand_market/crwdmarket_icon_w.svg';
import useViewport from 'hooks/useViewport';
import { getMarketContent } from 'utils/marketContent';
import { ThemeSwitch } from '../../theme/index';
import './Nav.scss';

export default function Nav(): ReactElement {
  const { quantity, hideNav, setHideNav } = useCartContext();
  const [model, setModel] = useState<MenuItem[]>([]);

  const login = useLogin();
  const logout = useLogout();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, user } = useAuth0();
  const [isDarkFromLocalStroage] = useLocalStorage<boolean>('isDark', true);
  const [, setIsAMM] = useState<boolean>(true);
  const navLogo = isDarkFromLocalStroage ? LogoLight : Logo;
  const [marketPlaceLogo, setMarketPlaceLogo] = useState<any>();
  const { width } = useViewport();
  const isMobileWidth = width < 992;
  const [isMobile, setIsMobile] = useState(isMobileWidth);
  const marketplaceDomain = useMarketplaceDomain();

  const market = getMarketContent();
  const data = market.navigation;
  const marketName = market.market;

  useEffect(() => {
    if (
      !['advancedmedicine', 'localhost'].includes(marketplaceDomain as string)
    ) {
      localStorage.setItem('isDark', 'false');
      setIsAMM(false);
    }
  }, [marketplaceDomain]);

  useEffect(() => {
    if (isMobileWidth !== isMobile) {
      setIsMobile(isMobileWidth);
    }
  }, [width, isMobile, isMobileWidth]);

  useEffect(() => {
    const marketPlaceLogo = isDarkFromLocalStroage
      ? data.logoDesktopLight
      : data.logoDesktopDark;
    setMarketPlaceLogo(marketPlaceLogo);
  }, [data.logoDesktopDark, data.logoDesktopLight, isDarkFromLocalStroage]);

  useEffect(() => {
    const searchEntries = Object.fromEntries(searchParams.entries());
    if (searchEntries['hide-nav']) {
      setHideNav(searchEntries['hide-nav'] === 'true');
    }
  }, [searchParams, setHideNav]);

  useEffect(() => {
    if (isAuthenticated) {
      setModel([
        {
          label: user?.name || user?.email || 'User',
          icon: 'pi pi-user',
          items: loggedInItems,
          className: 'nav-email mr-2',
        },
      ]);
    } else {
      setModel([...loggedOutItems]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const loggedInItems = [
    {
      label: 'Orders',
      command: () => navigate('/orders'),
    },
    {
      label: 'Logout',
      command: () => logout(),
    },
  ];

  const loggedOutItems = [
    {
      label: 'Login',
      icon: 'pi pi-user',
      command: () => login(),
    },
  ];
  if (hideNav) {
    return <></>;
  }

  return (
    <div className="nav m-0 w-screen">
      {isMobile ? (
        <div>
          <Menubar
            model={model}
            className="store-nav-mobile-menu py-4 px-2 flex items-baseline "
            start={
              <div className="flex flex-direction-row align-items-center">
                {marketPlaceLogo && marketName !== 'crwdmarket' && (
                  <Link to="/" className="mb-0 pb-0">
                    <img
                      src={`${marketPlaceLogo}`}
                      alt={marketName}
                      className="crwd-nav-logo mr-4"
                      style={{ maxWidth: '40px', minWidth: '40px' }}
                    />
                  </Link>
                )}
                <Link to="/" className="mb-0 pb-0">
                  <img
                    src={`${navLogo}`}
                    alt="CRWD logo"
                    className="crwd-nav-logo"
                    style={{ maxWidth: '100px', minWidth: '100px' }}
                  />
                </Link>
              </div>
            }
            end={
              <div className="flex align-items-center">
                <div className="mx-2">
                  {' '}
                  <ThemeSwitch />{' '}
                </div>

                <div className="cart-wrapper">
                  <Link to="/cart">
                    <i className="pi pi-fw pi-shopping-cart" />
                    <span
                      className={`nav-cart-quantity  ${
                        Number(quantity) === 0 ? 'hide' : ''
                      }`}
                    >
                      {quantity}
                    </span>
                  </Link>
                </div>
              </div>
            }
          />
        </div>
      ) : (
        <div
          className="px-3"
          style={{
            maxWidth: '1140px',
            margin: '0 auto',
            height: 'max-content',
          }}
        >
          <ul className="pr-2">
            <li className="flex flex-row align-items-center">
              {marketPlaceLogo && marketName !== 'crwdmarket' && (
                <Link to="/" className="mb-0 pb-0">
                  <img
                    src={`${marketPlaceLogo}`}
                    alt={marketName}
                    className="crwd-nav-logo mr-4"
                    style={{ maxWidth: '75px', minWidth: '50px' }}
                  />
                </Link>
              )}

              <Link to="/">
                <img
                  src={`${navLogo}`}
                  alt="CRWD logo"
                  className="crwd-nav-logo"
                />
              </Link>
            </li>
            <li>
              <div className="nav--user-info">
                <TieredMenu model={model} />
                <ThemeSwitch />{' '}
                <div className="cart-wrapper">
                  <Link to="/cart">
                    <i className="pi pi-fw pi-shopping-cart" />
                    <span className={`${Number(quantity) === 0 ? 'hide' : ''}`}>
                      {quantity}
                    </span>
                  </Link>
                </div>
              </div>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
}
